import * as Sentry from '@sentry/react';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RecordingErrorModal from '@/components/RecordingErrorModal';
import { useAuthContext } from '@/contexts/authContext';
import {
  MicrophonePermissionState,
  UseMicrophonePermissionReturn,
  useMicrophonePermission,
} from '@/hooks/audio/useMicrophonePermission';
import { ExpectedMicrophoneAcquisitionFailure, getExpectedMicrophoneAcquisitionFailure } from '@/utils/sentry';

export type MicrophoneRecoverySurface = 'activity_session' | 'conversation' | 'practice' | 'live_session';

interface MicrophonePermissionContextValue extends UseMicrophonePermissionReturn {
  showPermissionModal: boolean;
  handlePermissionError: (error: unknown, surface: MicrophoneRecoverySurface) => boolean;
  dismissPermissionModal: () => void;
}

const MicrophonePermissionContext = createContext<MicrophonePermissionContextValue | undefined>(undefined);

export interface MicrophonePermissionGuardProps {
  children: React.ReactNode;
  customModal?: React.ReactNode;
  onPermissionDenied?: () => void;
  autoShowModal?: boolean;
  // For a guard inside a full-screen dialog, whose recovery modal must sit above it.
  modalDialogClassname?: string;
}

export const MicrophonePermissionGuard: React.FC<MicrophonePermissionGuardProps> = ({
  children,
  customModal,
  onPermissionDenied,
  autoShowModal = true,
  modalDialogClassname,
}) => {
  const { t } = useTranslation('activitySession');
  const { user } = useAuthContext();
  const micPermission = useMicrophonePermission();
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [recoveryFailure, setRecoveryFailure] = useState<ExpectedMicrophoneAcquisitionFailure>();

  const handlePermissionError = useCallback(
    (error: unknown, surface: MicrophoneRecoverySurface) => {
      const failure = getExpectedMicrophoneAcquisitionFailure(error);
      if (!failure) {
        return false;
      }

      Sentry.metrics.count('microphone.acquisition.expected_failure', 1, {
        attributes: { reason: failure, surface },
      });

      const recoveryLogKey = `microphone-permission-recovery:${failure}:${surface}`;
      if (!sessionStorage.getItem(recoveryLogKey)) {
        Sentry.logger.info('microphone.permission_recovery_shown', { reason: failure, surface, userId: user?.id });
        sessionStorage.setItem(recoveryLogKey, 'true');
      }

      if (autoShowModal) {
        setRecoveryFailure(failure);
        setShowPermissionModal(true);
      }
      onPermissionDenied?.();
      return true;
    },
    [autoShowModal, onPermissionDenied, user?.id]
  );

  const dismissPermissionModal = useCallback(() => {
    setShowPermissionModal(false);
    setRecoveryFailure(undefined);
  }, []);

  const handleRequestPermissionAgain = useCallback(async () => {
    const currentState = await micPermission.checkPermission();
    if (currentState === MicrophonePermissionState.DENIED) {
      dismissPermissionModal();
      return;
    }

    const granted = await micPermission.requestPermission();
    if (granted) {
      setShowPermissionModal(false);
      setRecoveryFailure(undefined);
    }
  }, [dismissPermissionModal, micPermission]);

  const contextValue: MicrophonePermissionContextValue = {
    ...micPermission,
    showPermissionModal,
    handlePermissionError,
    dismissPermissionModal,
  };

  const isPermanentlyDenied =
    recoveryFailure === 'permission_denied' && micPermission.permissionState === MicrophonePermissionState.DENIED;

  const modalTitle = isPermanentlyDenied ? t('error.recording_permission_blocked_title') : undefined;
  const modalDescription = isPermanentlyDenied ? t('error.recording_permission_blocked_description') : undefined;
  const modalButtonText = isPermanentlyDenied ? t('error.recording_permission_blocked_button_text') : undefined;

  return (
    <MicrophonePermissionContext.Provider value={contextValue}>
      {children}

      {customModal ? (
        customModal
      ) : (
        <RecordingErrorModal
          open={showPermissionModal}
          onClose={dismissPermissionModal}
          onButtonClick={handleRequestPermissionAgain}
          errorType={recoveryFailure === 'device_not_found' ? 'no-device' : 'permission'}
          title={modalTitle}
          description={modalDescription}
          buttonText={modalButtonText}
          dialogClassname={modalDialogClassname}
        />
      )}
    </MicrophonePermissionContext.Provider>
  );
};

export const useMicrophonePermissionContext = (): MicrophonePermissionContextValue | null => {
  const context = useContext(MicrophonePermissionContext);
  return context ?? null;
};
